/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/drawerSectionPrefs.ts
 * FILE_ID: UI-KANBAN-DRAWER-SECTION-PREFS-001
 * PURPOSE: The per-user expand/collapse preference state for the Card-tab
 *   collapsed sections (ARC-4 drawer IA — progressive disclosure), plus the
 *   one-release "classic layout" revert flag.
 * WHY: ARC-4 U02 — operator: the drawer is "a bit crowded and unviewable".
 *   Everything below the ACTION spine is now a collapsed-by-default section
 *   with a count; an operator's expand choices persist per user (two-tier:
 *   localStorage gate + the platform view-preference DB store — REUSED, not
 *   a new /api/kanban/prefs/ fork, per the reuse mandate).
 * FLOW:
 *   ┌────────────────┐   ┌──────────────────────┐   ┌──────────────────────┐
 *   │ section toggle │ → │ prefs {expanded map} │ → │ localStorage + DB    │
 *   │ (user click)   │   │ isSectionExpanded    │   │ view-preference tier │
 *   └────────────────┘   └──────────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - @quotech/tenant-shared/preferences/dbViewPreference: the shared
 *     two-tier store (same engine useBoardViewPrefSync rides).
 * RELATED:
 *   - ./CollapsibleSection.tsx — the presentational section shell.
 *   - ./CardTabContent.tsx — the host that owns one prefs instance.
 * NOTES FOR FUTURE AI: Classic expand is localStorage kanban:drawer-ia-classic.
 *   This-board writes a :boardId suffix. The drawer reads board, then global.
 *   Do not fold this into /api/kanban/prefs/.
 */
import { useCallback, useEffect, useState } from 'react'

import { useDbViewPreference } from '@quotech/tenant-shared/preferences/useDbViewPreference'

import { useOptionalKanbanSettingsPreferences } from '../../../preferences/KanbanSettingsPreferenceContext'

/**
 * The named drawer slots (ARC-4 owns the SHELL; other arcs own contents):
 *   - 'entries'  — ARC-3's in-card sub-entry table (wired as CollapsibleSection).
 *   - email hide lives on kanban:card-tab-layout, not this expand map.
 *   - the rest   — existing sections this arc wraps in CollapsibleSection.
 */
export const DRAWER_SECTION_IDS = [
  'members',
  'labels',
  'checklists',
  'subcards',
  'linked',
  'activity',
  'entries',
  // Arc A (email-card-boards unification) — the drawer's placements
  // management section ("Boards & lanes").
  'placements',
] as const

export type DrawerSectionId = (typeof DRAWER_SECTION_IDS)[number]

export interface DrawerSectionPrefs {
  /** Per-section expand OVERRIDES. Absent key = the section default
   *  (collapsed). Only user toggles are stored, so a future default
   *  change reaches everyone who never expressed a preference. */
  expanded: Partial<Record<DrawerSectionId, boolean>>
}

export const DRAWER_SECTION_PREFS_SCOPE = 'kanban:drawer-sections'

/** Classic expand-all flag. Global key is unscoped. This-board writes
 *  `${DRAWER_CLASSIC_FLAG_KEY}:${boardId}` and the drawer reads board then global. */
export const DRAWER_CLASSIC_FLAG_KEY = 'kanban:drawer-ia-classic'

export function drawerClassicFlagKey(boardId?: string): string {
  return boardId ? `${DRAWER_CLASSIC_FLAG_KEY}:${boardId}` : DRAWER_CLASSIC_FLAG_KEY
}

export function resolveDrawerClassicFlag(
  getItem: (key: string) => string | null,
  boardId?: string,
): boolean {
  if (boardId) {
    const scoped = getItem(drawerClassicFlagKey(boardId))
    if (scoped === '1') return true
    if (scoped === '0') return false
  }
  return getItem(DRAWER_CLASSIC_FLAG_KEY) === '1'
}

export function readDrawerClassicFlag(boardId?: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return resolveDrawerClassicFlag((key) => window.localStorage.getItem(key), boardId)
  } catch {
    return false
  }
}

export const DRAWER_CLASSIC_CHANGE_EVENT = 'kanban:drawer-ia-classic-change'

export function writeDrawerClassicFlag(on: boolean, boardId?: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(drawerClassicFlagKey(boardId), on ? '1' : '0')
  } catch {
    /* quota / disabled storage */
  }
  window.dispatchEvent(new CustomEvent(DRAWER_CLASSIC_CHANGE_EVENT))
}

const DEFAULTS: DrawerSectionPrefs = { expanded: {} }

/** Coerce an untrusted blob into a valid prefs shape — unknown keys and
 *  non-boolean values are dropped so a corrupt cache can't poison state. */
export function coerceDrawerSectionPrefs(raw: unknown): DrawerSectionPrefs {
  if (!raw || typeof raw !== 'object') return { expanded: {} }
  const rawExpanded = (raw as { expanded?: unknown }).expanded
  const expanded: DrawerSectionPrefs['expanded'] = {}
  if (rawExpanded && typeof rawExpanded === 'object') {
    for (const id of DRAWER_SECTION_IDS) {
      const v = (rawExpanded as Record<string, unknown>)[id]
      if (typeof v === 'boolean') expanded[id] = v
    }
  }
  return { expanded }
}

/**
 * isSectionExpanded — pure: should section `id` render expanded?
 * Classic mode forces everything open (the revert behaviour); otherwise
 * the user's stored override wins, else collapsed-by-default.
 */
export function isSectionExpanded(
  prefs: DrawerSectionPrefs,
  id: DrawerSectionId,
  classic: boolean,
): boolean {
  if (classic) return true
  return prefs.expanded[id] ?? false
}

/** applyToggle — pure state transition: flip one section's expand state. */
export function applyToggle(
  prefs: DrawerSectionPrefs,
  id: DrawerSectionId,
  expanded: boolean,
): DrawerSectionPrefs {
  return { expanded: { ...prefs.expanded, [id]: expanded } }
}

export interface UseDrawerSectionPrefsResult {
  /** True → render everything expanded (pre-ARC-4 layout). */
  classic: boolean
  isExpanded: (id: DrawerSectionId) => boolean
  toggle: (id: DrawerSectionId) => void
}

/**
 * Bind drawer expansion actions to the board-level preference runtime.
 *
 * Inputs: none; the nearest board provider supplies tenant/user/scope identity.
 * Output: classic flag plus expanded selector and toggle transition.
 * Side effects: provider-backed writes, or local-only writes in isolated renders.
 * Failure modes: durable errors remain provider state without losing local state.
 */
export function useDrawerSectionPrefs(): UseDrawerSectionPrefsResult {
  const runtime = useOptionalKanbanSettingsPreferences()
  const fallback = useDbViewPreference({
    scopeKey: DRAWER_SECTION_PREFS_SCOPE,
    schemaVersion: 1,
    defaults: DEFAULTS,
    coerce: (raw) => coerceDrawerSectionPrefs(raw),
    hydrateFromServer: false,
  })
  const preference = runtime?.drawerSections ?? fallback
  const boardId = runtime?.boardId
  const [classic, setClassic] = useState(() => readDrawerClassicFlag(boardId))

  useEffect(() => {
    const sync = () => setClassic(readDrawerClassicFlag(boardId))
    sync()
    window.addEventListener(DRAWER_CLASSIC_CHANGE_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(DRAWER_CLASSIC_CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [boardId])

  const isExpanded = useCallback(
    (id: DrawerSectionId) => isSectionExpanded(preference.value, id, classic),
    [preference.value, classic],
  )

  const toggle = useCallback(
    (id: DrawerSectionId) => {
      preference.setValue((previous) => {
        return applyToggle(
          previous,
          id,
          !isSectionExpanded(previous, id, classic),
        )
      })
    },
    [classic, preference],
  )

  return { classic, isExpanded, toggle }
}
